import React, { useState, useEffect, useMemo } from 'react';
import { getGames, recordResult, getSyncPreview, syncGameResult } from '../services/api';
import CollapsibleRound from '../components/CollapsibleRound';

// Extracted so it can manage its own preview/selection state independent
// of the main scheduled/completed games list above it, and only fetches
// when actually opened -- most tournaments won't have a canonical source
// at all, so there's no reason to call sync-preview on every page load.
function SyncGameResults({ showFeedback, activeTournament, onSynced }) {
    const [open,       setOpen]       = useState(false);
    const [loading,    setLoading]   = useState(false);
    const [preview,    setPreview]   = useState(null); // { source, structuralMismatch, rounds } | null
    const [selected,   setSelected]  = useState({});   // { [game_id]: syncWinnerId }
    const [applying,   setApplying]  = useState(false);

    const checkSync = async () => {
        setOpen(true);
        setLoading(true);
        setSelected({});
        try {
            const res = await getSyncPreview(activeTournament.id);
            setPreview(res.data);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to check sync status', 'error');
            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const toggle = (game) => {
        setSelected(prev => {
            const next = { ...prev };
            if (next[game.game_id] !== undefined) delete next[game.game_id];
            else next[game.game_id] = game.syncWinnerId;
            return next;
        });
    };

    const allSyncableGames = useMemo(() => {
        if (!preview) return [];
        return preview.rounds.flatMap(r => r.games.filter(g => g.canSync && !g.missingScoringRules));
    }, [preview]);

    const selectAll = () => {
        const next = {};
        for (const g of allSyncableGames) next[g.game_id] = g.syncWinnerId;
        setSelected(next);
    };

    const selectedCount = Object.keys(selected).length;

    const applySync = async () => {
        setApplying(true);
        try {
            await Promise.all(
                Object.entries(selected).map(([gameId, winnerId]) => syncGameResult(gameId, winnerId))
            );
            showFeedback(`${selectedCount} result${selectedCount > 1 ? 's' : ''} synced from ${preview.source.name}`);
            setSelected({});
            onSynced();
            checkSync(); // refresh the comparison so it reflects the new state
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to sync results', 'error');
        } finally {
            setApplying(false);
        }
    };

    if (!open) {
        return (
            <div className="mb-8">
                <button onClick={checkSync}
                    className="bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white font-bold px-5 py-2 rounded-lg text-sm transition-colors">
                    Check for results to sync
                </button>
            </div>
        );
    }

    return (
        <div className="mb-10 bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-white">Sync game results</h3>
                <button onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:text-white transition-colors">Close</button>
            </div>

            {loading && <p className="text-gray-500 text-sm">Checking…</p>}

            {!loading && preview && preview.source === null && (
                <p className="text-gray-500 text-sm">
                    No canonical tournament found for this year and gender -- nothing to sync from.
                </p>
            )}

            {!loading && preview && preview.source && preview.structuralMismatch && (
                <p className="text-yellow-400 text-sm">
                    This tournament's bracket doesn't match {preview.source.name}'s team/seed structure,
                    so results can't be matched automatically.
                </p>
            )}

            {!loading && preview && preview.source && !preview.structuralMismatch && (
                <>
                    <p className="text-gray-400 text-sm mb-4">
                        Comparing against <span className="text-white font-bold">{preview.source.name}</span>.
                        Only games where that tournament has a result you don't already match are shown as syncable.
                    </p>

                    {allSyncableGames.length > 0 && (
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={selectAll}
                                className="text-xs text-orange-400 hover:text-orange-300 font-bold">
                                Select all {allSyncableGames.length} syncable game{allSyncableGames.length > 1 ? 's' : ''}
                            </button>
                            {selectedCount > 0 && (
                                <button onClick={applySync} disabled={applying}
                                    className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-black px-5 py-2 rounded-lg text-sm transition-colors">
                                    {applying ? 'Syncing…' : `Sync ${selectedCount} selected`}
                                </button>
                            )}
                        </div>
                    )}

                    {preview.rounds.map(round => (
                        <div key={round.round_number} className="mb-5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">{round.round_name}</p>
                            <div className="space-y-2">
                                {round.games.map(game => (
                                    <div key={game.game_id}
                                        className={'flex items-center justify-between rounded-lg px-4 py-3 border ' +
                                            (game.inSync ? 'border-gray-800 bg-gray-950' :
                                             game.canSync && !game.missingScoringRules ? 'border-orange-800 bg-gray-950' :
                                             'border-gray-800 bg-gray-950 opacity-60')}>
                                        <div>
                                            <p className="text-sm text-gray-300">{game.team1_short} vs {game.team2_short}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                You: {game.organizer.status === 'final' ? game.organizer.winner_short : 'not entered'}
                                                {' — '}
                                                {preview.source.name}: {game.canonical ? (game.canonical.status === 'final' ? game.canonical.winner_short : 'not entered') : 'no matching game'}
                                            </p>
                                            {game.missingScoringRules && (
                                                <p className="text-xs text-yellow-500 mt-1">
                                                    This round is missing scoring rules for a pool with picks -- fix that before syncing.
                                                </p>
                                            )}
                                        </div>
                                        {game.inSync ? (
                                            <span className="text-xs text-green-400 font-bold">In sync</span>
                                        ) : game.canSync && !game.missingScoringRules ? (
                                            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                                                <input type="checkbox" checked={selected[game.game_id] !== undefined} onChange={() => toggle(game)} />
                                                Sync this
                                            </label>
                                        ) : (
                                            <span className="text-xs text-gray-600">—</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

function AdminGameResults({ showFeedback, activeTournament }) {
    const [games,           setGames]           = useState([]);
    const [selections,      setSelections]      = useState({});
    const [collapsedRounds, setCollapsedRounds] = useState(new Set());
    const [autoCollapsed,   setAutoCollapsed]   = useState(false);
    const [editingGameId,   setEditingGameId]   = useState(null);
    const [editSelection,   setEditSelection]   = useState(null); // { teamId, teamName }

    // Same fallback pattern as AdminSeeding.js: shows the tournament's
    // saved region_display_names (e.g. women's site-based names like
    // "Albany 1") if set, otherwise the plain internal East/West/South/
    // Midwest slot name -- unchanged for every men's tournament.
    const regionLabel = (region) => activeTournament?.region_display_names?.[region] || region;

    useEffect(() => {
        if (!activeTournament) return;
        getGames(activeTournament.id, null).then(res => setGames(res.data));
    }, [activeTournament]);

    const loadGames = () => {
        if (!activeTournament) return;
        getGames(activeTournament.id, null).then(res => setGames(res.data));
    };

    // Reset auto-collapse state whenever the tournament changes so we recompute
    // the default "active round expanded" view for the new tournament.
    useEffect(() => {
        setAutoCollapsed(false);
    }, [activeTournament]);

    const scheduledGames = games.filter(g => g.status === 'scheduled');
    const finalGames     = games.filter(g => g.status === 'final');

    // Group scheduled games by round, preserving backend sort order.
    const scheduledRounds = useMemo(() => {
        const map = {};
        for (const g of scheduledGames) {
            if (!map[g.round_number]) {
                map[g.round_number] = {
                    round_number: g.round_number,
                    round_name:   g.round_name,
                    games:        []
                };
            }
            map[g.round_number].games.push(g);
        }
        return Object.values(map).sort((a, b) => a.round_number - b.round_number);
    }, [scheduledGames]);

    // Group final games by round, same shape as scheduledRounds. Used to
    // render the "Completed games" section as collapsible-by-round so the
    // admin can scan past rounds without scrolling through every result.
    const finalRounds = useMemo(() => {
        const map = {};
        for (const g of finalGames) {
            if (!map[g.round_number]) {
                map[g.round_number] = {
                    round_number: g.round_number,
                    round_name:   g.round_name,
                    games:        []
                };
            }
            map[g.round_number].games.push(g);
        }
        return Object.values(map).sort((a, b) => a.round_number - b.round_number);
    }, [finalGames]);

    // Separate collapse state for completed rounds, kept independent from
    // the scheduled-rounds state so toggling one doesn't affect the other.
    const [collapsedFinalRounds, setCollapsedFinalRounds] = useState(new Set());
    const [autoCollapsedFinal,   setAutoCollapsedFinal]   = useState(false);

    useEffect(() => {
        setAutoCollapsedFinal(false);
    }, [activeTournament]);

    // On first load (and after tournament switch), collapse ALL completed
    // rounds. Admin's mental focus on this page is pending work, not history;
    // history is available on demand by clicking the round header.
    useEffect(() => {
        if (autoCollapsedFinal) return;
        if (finalRounds.length === 0) return;
        setCollapsedFinalRounds(new Set(finalRounds.map(r => r.round_number)));
        setAutoCollapsedFinal(true);
    }, [finalRounds, autoCollapsedFinal]);

    const toggleFinalRound = (roundNumber) => {
        setCollapsedFinalRounds(prev => {
            const next = new Set(prev);
            if (next.has(roundNumber)) next.delete(roundNumber);
            else next.add(roundNumber);
            return next;
        });
    };

    // On first load (and after tournament switch), collapse all rounds except
    // the lowest-numbered round that has scheduled games — that's the round
    // the admin is most likely working on.
    useEffect(() => {
        if (autoCollapsed) return;
        if (scheduledRounds.length === 0) return;
        const activeRoundNumber = scheduledRounds[0].round_number;
        const initialCollapsed = new Set(
            scheduledRounds
                .filter(r => r.round_number !== activeRoundNumber)
                .map(r => r.round_number)
        );
        setCollapsedRounds(initialCollapsed);
        setAutoCollapsed(true);
    }, [scheduledRounds, autoCollapsed]);

    const toggleRound = (roundNumber) => {
        setCollapsedRounds(prev => {
            const next = new Set(prev);
            if (next.has(roundNumber)) next.delete(roundNumber);
            else next.add(roundNumber);
            return next;
        });
    };

    const selectWinner = (game, teamId, teamName) => {
        setSelections(prev => ({
            ...prev,
            [game.id]: { game, teamId, teamName }
        }));
    };

    const clearSelection = (gameId) => {
        setSelections(prev => {
            const next = { ...prev };
            delete next[gameId];
            return next;
        });
    };

    const pendingCount = Object.keys(selections).length;

    const handleSubmit = async () => {
        try {
            await Promise.all(
                Object.values(selections).map(s => recordResult(s.game.id, s.teamId))
            );
            showFeedback(`${pendingCount} result${pendingCount > 1 ? 's' : ''} recorded`);
            setSelections({});
            loadGames();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to record results', 'error');
        }
    };

    const startEdit = (game) => {
        setEditingGameId(game.id);
        setEditSelection(null);
    };

    const cancelEdit = () => {
        setEditingGameId(null);
        setEditSelection(null);
    };

    const saveCorrection = async (game) => {
        if (!editSelection) return;
        try {
            await recordResult(game.id, editSelection.teamId);
            showFeedback(`Result corrected: ${editSelection.teamName} now shows as the winner`);
            setEditingGameId(null);
            setEditSelection(null);
            loadGames();
        } catch (err) {
            // Covers both blocking conditions from the backend (next round
            // already decided, or a participant already picked a specific
            // team downstream) -- surfaced verbatim rather than guessed at,
            // since the backend's message already explains which one and
            // that it needs a manual fix.
            showFeedback(err.response?.data?.error || 'Failed to correct result', 'error');
        }
    };

    if (!activeTournament) return <p className="text-gray-500 text-sm">No tournament selected.</p>;

    return (
        <div>
            <SyncGameResults showFeedback={showFeedback} activeTournament={activeTournament} onSynced={loadGames} />

            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-white">
                    Scheduled games
                    <span className="ml-2 text-sm font-normal text-gray-500">
                        ({scheduledGames.length} remaining)
                    </span>
                </h3>
                {pendingCount > 0 && (
                    <button onClick={handleSubmit}
                        className="bg-green-600 hover:bg-green-500 text-white font-black px-5 py-2 rounded-lg text-sm transition-colors">
                        Submit {pendingCount} Result{pendingCount > 1 ? 's' : ''}
                    </button>
                )}
            </div>

            {scheduledGames.length === 0 && (
                <p className="text-gray-500 text-sm mb-8">No scheduled games.</p>
            )}

            <div className="mb-10">
                {scheduledRounds.map(round => {
                    const pendingInRound = round.games.filter(g => selections[g.id]).length;
                    const headerExtra = pendingInRound > 0
                        ? `(${round.games.length} remaining — ${pendingInRound} selected)`
                        : `(${round.games.length} remaining)`;
                    return (
                        <CollapsibleRound
                            key={round.round_number}
                            round={round}
                            headerExtra={headerExtra}
                            isCollapsed={collapsedRounds.has(round.round_number)}
                            onToggle={() => toggleRound(round.round_number)}
                        >
                            <div className="space-y-3">
                                {round.games.map(game => {
                                    const selection = selections[game.id];
                                    return (
                                        <div key={game.id}
                                            className={'bg-gray-900 border rounded-xl px-5 py-4 transition-colors ' +
                                                (selection ? 'border-green-700' : 'border-gray-800')}>
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    {regionLabel(game.team1_region)}
                                                </p>
                                                {selection && (
                                                    <button onClick={() => clearSelection(game.id)}
                                                        className="text-xs text-gray-500 hover:text-red-400 transition-colors">
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => selectWinner(game, game.team1_id, game.team1_name)}
                                                    className={'flex-1 border font-bold py-2 px-4 rounded-lg transition-colors text-sm ' +
                                                        (selection?.teamId === game.team1_id
                                                            ? 'bg-green-700 border-green-500 text-white'
                                                            : selection?.teamId === game.team2_id
                                                                ? 'bg-gray-900 border-gray-700 text-gray-500'
                                                                : 'bg-gray-800 hover:bg-blue-900 border-gray-700 hover:border-blue-600 text-white')}>
                                                    {game.team1_seed}. {game.team1_name}
                                                </button>
                                                <span className="text-gray-600 font-bold">vs</span>
                                                <button
                                                    onClick={() => selectWinner(game, game.team2_id, game.team2_name)}
                                                    className={'flex-1 border font-bold py-2 px-4 rounded-lg transition-colors text-sm ' +
                                                        (selection?.teamId === game.team2_id
                                                            ? 'bg-green-700 border-green-500 text-white'
                                                            : selection?.teamId === game.team1_id
                                                                ? 'bg-gray-900 border-gray-700 text-gray-500'
                                                                : 'bg-gray-800 hover:bg-blue-900 border-gray-700 hover:border-blue-600 text-white')}>
                                                    {game.team2_seed}. {game.team2_name}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CollapsibleRound>
                    );
                })}
            </div>

            {pendingCount > 0 && (
                <div className="sticky bottom-4 flex justify-center mb-8">
                    <button onClick={handleSubmit}
                        className="bg-green-600 hover:bg-green-500 text-white font-black px-8 py-3 rounded-xl text-sm shadow-lg transition-colors">
                        Submit {pendingCount} Result{pendingCount > 1 ? 's' : ''}
                    </button>
                </div>
            )}

            <h3 className="text-lg font-black text-white mb-4">
                Completed games
                <span className="ml-2 text-sm font-normal text-gray-500">
                    ({finalGames.length} total)
                </span>
            </h3>
            {finalRounds.map(round => (
                <CollapsibleRound
                    key={`final-${round.round_number}`}
                    round={round}
                    headerExtra={`(${round.games.length} ${round.games.length === 1 ? 'game' : 'games'})`}
                    isCollapsed={collapsedFinalRounds.has(round.round_number)}
                    onToggle={() => toggleFinalRound(round.round_number)}
                >
                    <div className="space-y-2">
                        {round.games.map(game => {
                            const isEditing = editingGameId === game.id;
                            if (isEditing) {
                                return (
                                    <div key={game.id} className="bg-gray-900 border border-orange-700 rounded-xl px-5 py-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                {game.round_name} — {regionLabel(game.team1_region)} — correcting result
                                            </p>
                                            <button onClick={cancelEdit}
                                                className="text-xs text-gray-500 hover:text-white transition-colors">
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-3 mb-3">
                                            <button
                                                onClick={() => setEditSelection({ teamId: game.team1_id, teamName: game.team1_short })}
                                                className={'flex-1 border font-bold py-2 px-4 rounded-lg transition-colors text-sm ' +
                                                    (editSelection?.teamId === game.team1_id
                                                        ? 'bg-green-700 border-green-500 text-white'
                                                        : 'bg-gray-800 hover:bg-blue-900 border-gray-700 hover:border-blue-600 text-white')}>
                                                {game.team1_short}
                                            </button>
                                            <span className="text-gray-600 font-bold">vs</span>
                                            <button
                                                onClick={() => setEditSelection({ teamId: game.team2_id, teamName: game.team2_short })}
                                                className={'flex-1 border font-bold py-2 px-4 rounded-lg transition-colors text-sm ' +
                                                    (editSelection?.teamId === game.team2_id
                                                        ? 'bg-green-700 border-green-500 text-white'
                                                        : 'bg-gray-800 hover:bg-blue-900 border-gray-700 hover:border-blue-600 text-white')}>
                                                {game.team2_short}
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => saveCorrection(game)}
                                            disabled={!editSelection}
                                            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bold px-5 py-2 rounded-lg text-sm transition-colors">
                                            Save correction
                                        </button>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Only works if the next round's game for this matchup hasn't been decided yet,
                                            and no one has picked a specific team there based on this result.
                                        </p>
                                    </div>
                                );
                            }
                            return (
                                <div key={game.id} className="bg-gray-900 border border-gray-800 rounded-lg px-5 py-3 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                                            {game.round_name} — {regionLabel(game.team1_region)}
                                        </p>
                                        <p className="text-sm text-gray-400 mt-0.5">
                                            {game.team1_short} vs {game.team2_short}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 uppercase">Winner</p>
                                            <p className="text-sm font-black text-green-400">{game.winner_name}</p>
                                        </div>
                                        <button onClick={() => startEdit(game)}
                                            className="text-xs text-gray-500 hover:text-orange-400 transition-colors">
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CollapsibleRound>
            ))}
        </div>
    );
}

export default AdminGameResults;